import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ColoredMenuDownIcon } from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Search from 'Components/DesignSystem/Search/Search';
import Radio from 'Components/DesignSystem/Radio/Radio';
import CustomAttributeTag from 'Components/Common/CustomAttributeTag';

interface Option {
    value: string;
    /**
     * Kept a plain string ON PURPOSE (AB#14859): it is lowercased for the search filter and
     * reused as the trigger's readonly display text, so widening it to a ReactNode would break
     * both. The custom-attribute glyph rides on `isCustom` instead and is rendered beside it.
     */
    label: string;
    isCustom?: boolean;
}

interface SingleSelectWithCreateProps {
    value: string;
    onChange: (value: string) => void;
    options: Option[];
    onCreateNew: () => void;
    placeholder?: string;
    disabled?: boolean;
    loadOptions?: (search: string, page: number) => Promise<{ options: Option[]; hasMore: boolean }>;
}

const SingleSelectWithCreate = ({
    value,
    onChange,
    options,
    onCreateNew,
    placeholder = "Select attribute",
    disabled = false,
    loadOptions,
}: SingleSelectWithCreateProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Popup position (viewport-relative). Using position:fixed avoids being
    // clipped by ancestor scroll containers such as the mapping tables'
    // `table-container` wrapper, which has overflowX:auto — CSS normalizes
    // that to effectively overflowY:auto too, clipping any absolutely
    // positioned dropdown that extends past the cell's bottom.
    const [popupPos, setPopupPos] = useState<{ top: number; left: number; width: number } | null>(null);

    // API pagination state
    const [apiOptions, setApiOptions] = useState<Option[]>([]);
    const [apiPage, setApiPage] = useState(1);
    const [apiHasMore, setApiHasMore] = useState(false);
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const sentinelRef = useRef<HTMLDivElement>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressNextFetchRef = useRef(false);

    // Recalculate popup position whenever it opens, and follow scroll/resize
    // so the popup stays glued to the trigger while it's open.
    useEffect(() => {
        if (!isOpen) {
            setPopupPos(null);
            return;
        }
        const updatePos = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            // Clamp left so the fixed-positioned popup never extends past the
            // viewport's right edge — a popup whose left+width exceeds
            // innerWidth triggers a body-level horizontal scrollbar that the
            // user explicitly doesn't want.
            const maxLeft = Math.max(0, window.innerWidth - rect.width - 4);
            const clampedLeft = Math.max(4, Math.min(rect.left, maxLeft));
            // Default to opening downward (top = rect.bottom). When the trigger
            // sits near the viewport bottom there isn't enough room below for
            // the 240px (tw-max-h-60) popup — without this flip the panel
            // renders off-screen with only a sliver visible. Open upward
            // instead when above has more room.
            const POPUP_MAX = 240; // matches tw-max-h-60
            const GUTTER = 4;
            const spaceBelow = window.innerHeight - rect.bottom - GUTTER;
            const spaceAbove = rect.top - GUTTER;
            const top = (spaceBelow >= POPUP_MAX || spaceBelow >= spaceAbove)
                ? rect.bottom
                : rect.top - Math.min(POPUP_MAX, Math.max(0, spaceAbove));
            setPopupPos({ top, left: clampedLeft, width: rect.width });
        };
        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (isOpen) {
                    closeDropdown();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (!loadOptions || !isOpen) return;
        if (suppressNextFetchRef.current) {
            suppressNextFetchRef.current = false;
            return;
        }
        setIsApiLoading(true);
        loadOptions(debouncedSearch, apiPage)
            .then(({ options: newOpts, hasMore }) => {
                setApiHasMore(hasMore);
                setApiOptions(prev =>
                    apiPage === 1
                        ? newOpts
                        : [...prev, ...newOpts.filter(o => !prev.some(p => p.value === o.value))]
                );
            })
            .finally(() => setIsApiLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiPage, debouncedSearch, isOpen]);

    useEffect(() => {
        if (!sentinelRef.current || !apiHasMore || isApiLoading || !loadOptions) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setApiPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [apiHasMore, isApiLoading, loadOptions]);

    const mergedOptions = useMemo(() => {
        if (!loadOptions) {
            return searchTerm
                ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
                : options;
        }

        const selectedOption: Option | null = value
            ? (options.find(o => o.value === value)
                || apiOptions.find(o => o.value === value)
                || { value, label: value })
            : null;

        if (searchTerm) {
            if (searchTerm !== debouncedSearch) {
                return selectedOption ? [selectedOption] : [];
            }
            const apiWithoutSelected = apiOptions.filter(o => o.value !== value);
            return selectedOption ? [selectedOption, ...apiWithoutSelected] : apiWithoutSelected;
        }

        const localWithoutSelected = options.filter(o => o.value !== value);
        const localValues = new Set(options.map(o => o.value));
        const uniqueApiOptions = apiOptions.filter(o => !localValues.has(o.value) && o.value !== value);
        return selectedOption
            ? [selectedOption, ...localWithoutSelected, ...uniqueApiOptions]
            : [...options, ...uniqueApiOptions];
    }, [options, value, searchTerm, debouncedSearch, apiOptions, loadOptions]);

    const getDisplayLabel = (): string => {
        if (!value) return '';
        const option = options.find(opt => opt.value === value)
            || apiOptions.find(opt => opt.value === value);
        return option?.label || value;
    };

    const handleSelect = (optionValue: string | number) => {
        onChange(String(optionValue));
        closeDropdown();
    };

    const closeDropdown = () => {
        setIsOpen(false);
        setSearchTerm('');
        setApiOptions([]);
        setApiPage(1);
        setApiHasMore(false);
        setDebouncedSearch('');
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            if (isOpen) {
                closeDropdown();
            } else {
                setIsOpen(true);
            }
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        if (!loadOptions) return;
        suppressNextFetchRef.current = true;
        setApiOptions([]);
        setApiPage(1);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            suppressNextFetchRef.current = false;
            setDebouncedSearch(value);
        }, 500);
    };

    return (
        <div className="tw-relative tw-w-full" ref={dropdownRef}>
            <div className="tw-relative" ref={triggerRef}>
                <input data-testid="components-singleselectwithcreate-input-1"
                    type="text"
                    value={getDisplayLabel()}
                    readOnly
                    onClick={toggleDropdown}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`tw-w-full tw-py-2 tw-px-3 tw-pr-8 tw-border tw-rounded tw-bg-surface tw-text-sm tw-outline-none focus:tw-ring-1 focus:tw-ring-accent-hover tw-border-edge-strong tw-cursor-pointer ${disabled ? 'tw-bg-surface-sunken tw-cursor-not-allowed tw-text-ink-disabled placeholder:tw-text-ink-disabled' : 'tw-text-ink placeholder:tw-text-ink-secondary'
                        }`}
                    style={{
                        fontSize: "14px",
                        fontWeight: "500",
                    }}
                />
                <button aria-label="Expand" data-testid="components-singleselectwithcreate-button-2"
                    onClick={toggleDropdown}
                    className={`tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-p-1 ${disabled ? 'tw-cursor-not-allowed tw-opacity-50 tw-text-ink-tertiary' : 'tw-cursor-pointer tw-text-ink'
                        }`}
                    type="button"
                    disabled={disabled}
                >
                    <ColoredMenuDownIcon />
                </button>
            </div>

            {isOpen && !disabled && popupPos && (
                <div
                    className="tw-fixed tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-z-[9999] tw-max-h-60 tw-overflow-y-auto no-scrollbar"
                    style={{
                        top: `${popupPos.top}px`,
                        left: `${popupPos.left}px`,
                        width: `${popupPos.width}px`,
                    }}
                >
                    <div className="tw-py-1 tw-my-1">
                        {/* Create New Button */}
                        <div className="tw--ml-1 tw-mb-2">
                            <Button
                                variant="tertiary"
                                onClick={() => {
                                    closeDropdown();
                                    onCreateNew();
                                }}
                                className="hover:tw-bg-surface"
                            >
                                Create
                            </Button>
                        </div>

                        {/* Search Box */}
                        <div className="tw-px-3 tw-mb-2">
                            <Search
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                /* § 288 — gated on `loadOptions`, because without it
                                   `handleSearchChange` returns before arming the
                                   debounce: `debouncedSearch` would never catch up and
                                   the field would spin forever over what is really a
                                   local filter. With it, the inequality is the 500ms
                                   wait and `isApiLoading` the request. */
                                loading={!!loadOptions && (isApiLoading || searchTerm !== debouncedSearch)}
                                loadingLabel="Searching options"
                                clearable
                                searchIconPosition="end"
                            />
                        </div>

                        {/* Options */}
                        {mergedOptions.length === 0 && searchTerm && !isApiLoading ? (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                                No attributes found
                            </div>
                        ) : (
                            mergedOptions.map((option) => (
                                <div key={option.value} className="tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-1">
                                    {option.isCustom === true &&
                                        <CustomAttributeTag data-testid={`option-custom-tag-${option.value}`} />}
                                    <Radio
                                        label={option.label}
                                        value={option.value}
                                        checked={value === option.value}
                                        onChange={handleSelect}
                                    />
                                </div>
                            ))
                        )}

                        {mergedOptions.length === 0 && !searchTerm && !isApiLoading && (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                                No attributes available
                            </div>
                        )}

                        {/* Sentinel for infinite scroll */}
                        {loadOptions && apiHasMore && !isApiLoading && (
                            <div ref={sentinelRef} className="tw-h-1" />
                        )}

                        {/* Loading indicator */}
                        {isApiLoading && (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-secondary">
                                Loading...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleSelectWithCreate;
